// Cliente HTTP del exportable de viajes (LQ-1), mismo patron que
// requests/http.ts y multi-site/http.ts: token en el header, sin cookies ni
// redirecciones, y el JSON por el parser de @ucorp/contracts antes de tocar
// estado. El error sale tipado por status, nunca con el cuerpo remoto.
//
// El dia de hoy de la planta sale de la bandeja sin fecha (una sola fila):
// la API lo resuelve en la zona IANA de la planta, igual que en Viajes. El
// navegador no decide que dia es (regla 9).
import { ProviderTripsResponse, TripExportResponse } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { fileNameFromDisposition, operationalTodayPath, tripExportPath } from "../logic/trip-export.js";
import { TripExportError, type TripExportPort } from "./port.js";

function failure(status: number): TripExportError {
  if (status === 403) return new TripExportError("access");
  if (status === 401) return new TripExportError("session");
  if (status === 400) return new TripExportError("validation");
  return new TripExportError("unavailable");
}

export function createHttpTripExportPort(apiUrl: string, request: typeof fetch = fetch): TripExportPort {
  const base = apiUrl.replace(/\/+$/, "");

  async function get(session: PortalSession, path: string): Promise<Response> {
    let response: Response;
    try {
      response = await request(`${base}${path}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${session.accessToken}` },
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
      });
    } catch {
      throw new TripExportError("unavailable");
    }
    if (!response.ok) throw failure(response.status);
    return response;
  }

  async function json(response: Response): Promise<unknown> {
    try {
      return (await response.json()) as unknown;
    } catch {
      throw new TripExportError("unavailable");
    }
  }

  return {
    async today(session, siteId) {
      const parsed = ProviderTripsResponse.safeParse(await json(await get(session, operationalTodayPath(siteId))));
      if (!parsed.success) throw new TripExportError("unavailable");
      return { date: parsed.data.date, timezone: parsed.data.timezone };
    },
    async read(session, siteId, date) {
      const parsed = TripExportResponse.safeParse(await json(await get(session, tripExportPath(siteId, date, "json"))));
      // Un exportable de otra planta es una respuesta rota.
      if (!parsed.success || parsed.data.siteId !== siteId) throw new TripExportError("unavailable");
      return parsed.data;
    },
    async download(session, siteId, date, format) {
      const response = await get(session, tripExportPath(siteId, date, format));
      let blob: Blob;
      try {
        blob = await response.blob();
      } catch {
        throw new TripExportError("unavailable");
      }
      return { blob, fileName: fileNameFromDisposition(response.headers.get("Content-Disposition"), siteId, date, format) };
    },
  };
}
